import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { FUNCTIONALITIES, CRM_BASE } from "./config";
import { useAccess } from "../../hooks/useAccess";
import { isPlatformAdminUser } from "../../hooks/useIsReseller";

// Single shell for every CRM functionality. Derives the active functionality
// from the URL (/crm/<slug>/...), renders that functionality's sub-menu, and
// the matched section via <Outlet/>. Mirrors NuvexAccessLayout / SGLayout
// (Header + AppSidebarMenu + AppsSidebar + Outlet).
export default function CrmLayout() {
  const { t } = useTranslation("common");
  const { canAccessSectionOfApp } = useAccess();
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Active functionality from URL: /crm/<slug>/<item>
  const parts = location.pathname.split("/").filter(Boolean); // ["crm", slug, item]
  const activeSlug = parts[1] ?? FUNCTIONALITIES[0].slug;
  const functionality =
    FUNCTIONALITIES.find((f) => f.slug === activeSlug) ?? FUNCTIONALITIES[0];

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = functionality.menu
    .filter((item) =>
      canAccessSectionOfApp(`crm-${functionality.slug}`, item.id),
    )
    // Ítems sólo de plataforma (p. ej. Configuración → Demos, #1318).
    .filter((item) => !item.platformOnly || isPlatformAdminUser())
    .map((item) => {
    const base = `${CRM_BASE}/${functionality.slug}/${item.id}`;
    if (item.children?.length) {
      return {
        id: item.id,
        label: item.label,
        icon: item.icon,
        // Parent click navigates to its first child.
        path: `${base}/${item.children[0].id}`,
        children: item.children.map((c) => ({
          id: c.id,
          label: c.label,
          icon: c.icon,
          path: `${base}/${c.id}`,
        })),
      };
    }
    return { id: item.id, label: item.label, icon: item.icon, path: base };
  });

  const toggleSubmenu = (menuId: string) =>
    setExpandedMenus((prev) =>
      prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId],
    );

  // Auto-expand the parent whose child route is active (deep-link / refresh).
  useEffect(() => {
    const activeParent = functionality.menu.find((item) =>
      item.children?.some(
        (c) => location.pathname === `${CRM_BASE}/${functionality.slug}/${item.id}/${c.id}`,
      ),
    );
    if (activeParent) {
      setExpandedMenus((prev) =>
        prev.includes(activeParent.id) ? prev : [...prev, activeParent.id],
      );
    }
  }, [location.pathname, functionality]);

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="flex h-screen pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title="CRM"
        subtitle={functionality.name}
        onOpenAppsSidebar={openAppsSidebar}
        isAppsSidebarOpen={isAppsSidebarOpen}
        />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId, path) =>
            path
              ? location.pathname === path
              : location.pathname === `${CRM_BASE}/${functionality.slug}/${itemId}`
          }
          expandedMenus={expandedMenus}
          onToggleSubmenu={toggleSubmenu}
          footerStats={{
            label: functionality.name,
            items: [
              { label: "Secciones", value: menuItems.length },
              { label: "Módulos", value: FUNCTIONALITIES.length, className: "text-primary-500" },
            ],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={`crm-${functionality.slug}`}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
